import React from "react";
import { CalendarIcon, TrendingUpIcon, TrendingDownIcon, MinusIcon, TagIcon, Eye } from "lucide-react";
import { Radio, Empty } from "antd";
import { Typography } from "antd";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;

interface BidTestItem {
  name: string;
  date: string;
  jobConfigId: string;
}

interface BidGroup {
  [key: string]: any;
  "Bid Value": number;
}

interface BidOutputData {
  highest: BidGroup[];
  medium: BidGroup[];
  lowest: BidGroup[];
  segment_columns: string[];
  total_records: number;
}

interface Props {
  bidTests?: BidTestItem[];
  selectedJobConfigId?: string | null;
  onSelect?: (jobConfigId: string) => void;
  bidOutputData?: BidOutputData | null;
}

const TILE_CONFIG = [
  {
    key: "highest" as const,
    label: "Highest Bid Values",
    Icon: TrendingUpIcon,
    color: "text-[var(--success-card)]",
    bg: "bg-[var(--success-card-subtle)]",
    border: "border-[var(--success-card)]",
    valueBg: "bg-[var(--success-card)] text-white",
  },
  {
    key: "medium" as const,
    label: "Medium Bid Values",
    Icon: MinusIcon,
    color: "text-adrsta-blue",
    bg: "bg-[var(--blue-subtle)]",
    border: "border-[var(--blue)]",
    valueBg: "bg-adrsta-blue text-white",
  },
  {
    key: "lowest" as const,
    label: "Lowest Bid Values",
    Icon: TrendingDownIcon,
    color: "text-[var(--warning)]",
    bg: "bg-[var(--warning-subtle)]",
    border: "border-[var(--warning)]",
    valueBg: "bg-[var(--warning)] text-white",
  },
];

function formatColLabel(col: string): string {
  return col.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function capitalizeFirst(val: any): string {
  const s = String(val);
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function getBidRange(rows: BidGroup[]): string | null {
  const vals = rows
    .map((r) => r["Bid Value"])
    .filter((v) => typeof v === "number")
    .sort((a, b) => a - b);
  if (vals.length === 0) return null;
  if (vals.length === 1 || vals[0] === vals[vals.length - 1])
    return `${vals[0].toFixed(2)}`;
  return `${vals[0].toFixed(2)} – ${vals[vals.length - 1].toFixed(2)}`;
}

const BidDashboardCard: React.FC<Props> = ({
  bidTests = [],
  selectedJobConfigId,
  onSelect,
  bidOutputData,
}) => {
  const navigate = useNavigate();

  const selectedTest = bidTests.find((t) => t.jobConfigId === selectedJobConfigId);

  const handleView = (e: React.MouseEvent, test: BidTestItem) => {
    e.stopPropagation();
    navigate(`/bid-optimizer/${test.jobConfigId}`);
  };

  return (
    <div className="">
      {/* Header */}
      <div className="w-full mb-5 flex justify-center">
        <div className="bg-surface rounded-2xl shadow-sm p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 w-full border border-subtle-t">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full flex-shrink-0" style={{ background: "var(--blue-subtle)" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-adrsta-blue"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <Title level={1} className="m-0">
                Bid Optimizer Results
              </Title>
              <p className="text-base mt-[-10px] text-secondary-t">
                {selectedTest?.name || "No test selected"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate("/bid-optimizer")}
              className="px-4 py-2 bg-surface text-secondary-t rounded-lg shadow hover:bg-hover-bg border border-subtle-t transition"
            >
              View All
            </button>
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Left — test list with radio buttons */}
        <div className="col-span-1 md:col-span-4 bg-elevated p-4 rounded-xl shadow-sm border border-subtle-t">
          <h3 className="text-lg font-semibold text-secondary-t mb-4">
            Historical Bid Optimizer Results
          </h3>
          {bidTests.length === 0 ? (
            <Empty
              description="No completed tests available"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              className="my-4"
              imageStyle={{ height: 40 }}
            />
          ) : (
            <div className="space-y-3">
              {bidTests.slice(0, 5).map((test) => (
                <div
                  key={test.jobConfigId}
                  className="flex items-center gap-3 border-b pb-3 cursor-pointer"
                  onClick={() => onSelect?.(test.jobConfigId)}
                >
                  <Radio checked={selectedJobConfigId === test.jobConfigId} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary-t truncate">
                      {test.name}
                    </p>
                    <div className="flex items-center text-xs text-tertiary-t mt-1">
                      <CalendarIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span>{test.date}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleView(e, test)}
                    className="flex items-center gap-1 text-white text-xs px-3 py-1 rounded bg-adrsta-blue hover:bg-primary-dark whitespace-nowrap flex-shrink-0"
                  >
                    <Eye className="w-3 h-3" />
                    View Results
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right — three tiles stacked vertically */}
        <div className="col-span-1 md:col-span-8">
          {!bidOutputData ? (
            <div className="bg-elevated rounded-xl shadow-sm h-full flex items-center justify-center py-16 border border-subtle-t">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-secondary-t mb-2">No Data</h3>
                <p className="text-secondary-t text-sm">
                  Select a test to view bid multiplier results
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col">
                {TILE_CONFIG.map(({ key, label, Icon, color, bg, border, valueBg }, tileIdx) => {
                  const rows: BidGroup[] = bidOutputData[key] || [];
                  const segCols: string[] = bidOutputData.segment_columns || [];
                  const bidRange = getBidRange(rows);

                  return (
                    <div
                      key={key}
                      className={`${bg} border-x border-b ${tileIdx === 0 ? "border-t rounded-t-xl" : ""} ${tileIdx === TILE_CONFIG.length - 1 ? "rounded-b-xl" : ""} ${border} px-4 pt-3 pb-4`}
                    >
                      {/* Tile header — label + bid range */}
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className={`w-4 h-4 ${color}`} />
                        <span className={`text-sm font-semibold ${color}`}>{label}</span>
                        {bidRange && (
                          <>
                            <span className="text-tertiary-t text-xs mx-1">·</span>
                            <span className="text-xs text-secondary-t font-medium">
                              Bid Multiplier:
                            </span>
                            <span className={`${valueBg} text-xs font-bold px-2 py-0.5 rounded-md`}>
                              {bidRange}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Two segment cards side by side */}
                      <div className="grid grid-cols-2 gap-3">
                        {rows.length === 0 ? (
                          <p className="text-xs text-tertiary-t col-span-2">No data</p>
                        ) : (
                          rows.map((row, i) => (
                            <div key={i} className="bg-elevated rounded-lg shadow-sm overflow-hidden border border-subtle-t">
                              {segCols.map((col) =>
                                row[col] !== undefined && row[col] !== null && row[col] !== "" ? (
                                  <div
                                    key={col}
                                    className="flex items-center justify-between px-3 py-1.5 border-b border-subtle-t last:border-b-0"
                                  >
                                    <span className="text-xs font-medium text-tertiary-t flex items-center gap-1 flex-shrink-0">
                                      <TagIcon className="w-2.5 h-2.5 text-tertiary-t" />
                                      {formatColLabel(col)}
                                    </span>
                                    <span className="text-xs text-primary-t font-semibold max-w-[52%] text-right truncate">
                                      {capitalizeFirst(row[col])}
                                    </span>
                                  </div>
                                ) : null
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Segment total — shown once, below all tiles */}
              <p className="text-xs text-tertiary-t text-right mt-2 pr-1">
                {bidOutputData.total_records.toLocaleString()} total segments analyzed
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BidDashboardCard;
